import React from 'react';
import { Message } from './types';

interface MessageItemProps {
  message: Message;
  autoTranslate: boolean;
  selectedLanguageCode: string;
  isTranslating: boolean;
  onToggleTranslation: (messageId: string) => void;
  onTranslateMessage: (messageId: string) => void;
}

/**
 * Declarative message renderer - handles system, peer, and self messages
 */
export const MessageItem: React.FC<MessageItemProps> = ({
  message,
  autoTranslate,
  selectedLanguageCode,
  isTranslating,
  onToggleTranslation,
  onTranslateMessage
}) => {
  // System message
  if (message.sender === 'system') {
    return (
      <div className="flex justify-center mb-4">
        <div className="bg-gray-800/50 text-muted-foreground text-xs px-3 py-1 rounded-full">
          {message.text}
        </div>
      </div>
    );
  }

  // Peer message
  if (message.sender === 'peer') {
    const shouldShowTranslation = autoTranslate ? true : message.showTranslation;

    return (
      <div className="flex items-end mb-4">
        <div className="flex flex-col space-y-0.5 max-w-[75%]">
          <div className="bg-surface shadow-md rounded-t-lg rounded-r-lg p-3">
            <p className="text-sm">{message.text}</p>
            {/* Translation section */}
            {shouldShowTranslation && message.translation && (
              <div className="mt-2 pt-2 border-t border-gray-700">
                <div className="flex items-center mb-1">
                  <div className="flex items-center bg-gray-700 rounded px-1.5 py-0.5 text-xs">
                    <span>{selectedLanguageCode}</span>
                  </div>
                </div>
                <p className="text-sm text-gray-400 italic">{message.translation}</p>
                {!autoTranslate && (
                  <button
                    className="text-blue-400 text-xs mt-1 hover:underline"
                    onClick={() => onToggleTranslation(message.id)}
                  >
                    Hide translation
                  </button>
                )}
              </div>
            )}
            {/* Show translation button */}
            {!autoTranslate && !message.showTranslation && (
              <button
                className="text-blue-400 text-xs mt-1 hover:underline disabled:opacity-50"
                onClick={() => onTranslateMessage(message.id)}
                disabled={isTranslating}
              >
                {isTranslating ? 'Translating...' : 'Show translation'}
              </button>
            )}
          </div>
          <span className="text-xs text-muted-foreground pl-1">{message.time}</span>
        </div>
      </div>
    );
  }

  // Self message
  return (
    <div className="flex items-end justify-end mb-4">
      <div className="flex flex-col space-y-0.5 max-w-[75%] items-end">
        <div className="bg-primary/20 text-primary shadow-md rounded-t-lg rounded-l-lg p-3">
          <p className="text-sm">{message.text}</p>
          {/* Translation for self messages (only when auto-translate is on) */}
          {autoTranslate && message.translation && (
            <div className="mt-2 pt-2 border-t border-gray-700 text-left">
              <div className="flex items-center mb-1">
                <div className="flex items-center bg-gray-700 rounded px-1.5 py-0.5 text-xs">
                  <span>{selectedLanguageCode}</span>
                </div>
              </div>
              <p className="text-sm text-gray-400 italic">{message.translation}</p>
            </div>
          )}
        </div>

        <div className="flex items-center text-xs text-muted-foreground pr-1">
          <span>{message.time}</span>
          {message.status === 'read' && (
            <svg xmlns="http://www.w3.org/2000/svg"
                 className="ml-1 text-green-500 h-3 w-3" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                 strokeLinejoin="round">
              <path d="M18 6L7 17L2 12"/>
              <path d="M22 10L13 19L11 17"/>
            </svg>
          )}
        </div>
      </div>
    </div>
  );
};
